import React from 'react';
import { Sparkles, Star, Gem, Zap, Camera, Award } from 'lucide-react';

/**
 * Shared PackageCard Component
 * 
 * Verwendet sowohl im Frontend (CreditsPage) als auch im Admin Panel (Live-Vorschau)
 * 
 * Templates:
 * - "classic": Aktuelles SNAPSHOT Design (grüner Border, Icon oben)
 * - "modern": Card mit Shadow, Icon links, horizontal Layout
 * - "minimalist": Clean, text-only, dünner Border
 */

const ICON_MAP = {
  'sparkles': Sparkles,
  'star': Star,
  'gem': Gem,
  'zap': Zap,
  'camera': Camera,
  'award': Award
};

export default function PackageCard({ 
  pkg, 
  template = 'classic',
  accentColor = '#10B981',
  onBuyClick,
  previewMode = false
}) {
  const Icon = ICON_MAP[pkg.iconType] || Sparkles;
  
  // Template: Classic (Current SNAPSHOT Design)
  if (template === 'classic') {
    // Entscheide Hintergrund-Style
    let bgClass, borderClass, hoverClass;
    
    if (pkg.isPopular) {
      // Bestseller: Immer grüner Gradient
      bgClass = 'bg-gradient-to-br from-[#10B981]/10 to-black';
      borderClass = 'border-[#10B981]';
      hoverClass = 'glow-green';
    } else if (pkg.useGradient) {
      // Farbiger Hintergrund: Gradient mit Akzentfarbe
      bgClass = `bg-gradient-to-br to-black`;
      borderClass = 'border-white/30';
      hoverClass = 'hover:border-white/50';
    } else {
      // Standard: Schwarz/Transparent
      bgClass = 'bg-black/50';
      borderClass = 'border-white/20';
      hoverClass = 'hover:border-white/40';
    }
    
    return (
      <div
        className={`relative p-6 sm:p-8 rounded-2xl border-2 transition-all duration-300 ${
          !previewMode ? 'hover:scale-105' : ''
        } ${bgClass} ${borderClass} ${hoverClass}`}
        style={{
          borderColor: pkg.isPopular ? accentColor : undefined,
          backgroundImage: pkg.useGradient && !pkg.isPopular ? `linear-gradient(135deg, ${accentColor}15, black)` : undefined
        }}
        data-testid={`package-${pkg.id}`}
      >
        {/* Bestseller Badge */}
        {pkg.isPopular && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full">
            <p className="text-black font-bold text-xs sm:text-sm">Bestseller</p>
          </div>
        )}

        {/* Custom Badge */}
        {pkg.customBadge && (
          <div 
            className="absolute -top-3 right-4 px-3 py-1 rounded-full text-xs font-bold"
            style={{
              background: `linear-gradient(135deg, ${accentColor}, ${accentColor}dd)`,
              color: '#000'
            }}
          >
            {pkg.customBadge}
          </div>
        )}

        {/* Icon */}
        <div 
          className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
            pkg.isPopular ? 'glow-green' : ''
          }`}
          style={{
            background: pkg.isPopular 
              ? `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)` 
              : 'rgba(255,255,255,0.1)'
          }}
        >
          <Icon className={`w-8 h-8 ${pkg.isPopular ? 'text-black' : 'text-white'}`} style={{ color: pkg.isPopular ? '#000' : accentColor }} />
        </div>

        {/* Content */}
        <h3 className="text-2xl font-bold text-center mb-2">{pkg.name}</h3>
        <p className="text-white/60 text-center text-sm mb-4">{pkg.description}</p>
        {pkg.subtext && (
          <p className="text-center text-xs mb-4" style={{ color: `${accentColor}cc` }}>
            {pkg.subtext}
          </p>
        )}

        {/* Preis */}
        <div className="text-center mb-4">
          <p className="text-4xl font-bold">€{pkg.price.toFixed(2)}</p>
          <p className="text-xs text-white/40 mt-1">inkl. 19% MwSt.</p>
        </div>

        {/* Avatare + Bilder Info */}
        {pkg.avatarCount > 0 && (
          <div className="text-center mb-6">
            <p className="text-lg font-semibold" style={{ color: accentColor }}>
              {pkg.avatarCount === 1 ? '1 Avatar' : `${pkg.avatarCount} Avatare`} + {pkg.imageCount} Bilder
            </p>
          </div>
        )}
        
        {pkg.avatarCount === 0 && pkg.imageCount > 0 && (
          <div className="text-center mb-6">
            <p className="text-lg font-semibold" style={{ color: accentColor }}>
              {pkg.imageCount} Bilder
            </p>
          </div>
        )}

        {/* Buy Button */}
        {!previewMode && (
          <button
            onClick={() => onBuyClick && onBuyClick(pkg)}
            className={`w-full py-3 px-6 rounded-xl font-bold transition-all ${
              pkg.isPopular
                ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black hover:from-yellow-600 hover:to-orange-600'
                : 'text-black hover:opacity-90'
            }`}
            style={{
              backgroundColor: !pkg.isPopular ? accentColor : undefined
            }}
            data-testid={`buy-button-${pkg.id}`}
          >
            Jetzt kaufen
          </button>
        )}

        {previewMode && (
          <div className="w-full py-3 px-6 rounded-xl font-bold text-center bg-white/10 text-white/50 cursor-not-allowed">
            Vorschau-Modus
          </div>
        )}
      </div>
    );
  }

  // Template: Modern (Horizontal Card)
  if (template === 'modern') {
    return (
      <div
        className={`relative p-6 rounded-2xl bg-black/80 backdrop-blur-xl transition-all duration-300 ${
          !previewMode ? 'hover:translate-y-[-4px]' : ''
        }`}
        style={{
          boxShadow: `0 10px 40px ${accentColor}22, 0 4px 12px rgba(0,0,0,0.3)`
        }}
        data-testid={`package-${pkg.id}`}
      >
        <div className="flex items-start gap-6">
          {/* Icon (Links) */}
          <div 
            className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`
            }}
          >
            <Icon className="w-8 h-8 text-black" />
          </div>

          {/* Content */}
          <div className="flex-1">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-xl font-bold">{pkg.name}</h3>
                <p className="text-sm text-white/60 mt-1">{pkg.description}</p>
              </div>
              <div className="text-right ml-4">
                <p className="text-2xl font-bold">€{pkg.price.toFixed(2)}</p>
                <p className="text-xs text-white/40">inkl. 19% MwSt.</p>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-2 mt-4 mb-4">
              {pkg.avatarCount > 0 && (
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: accentColor }}></div>
                  <span className="text-white/70">
                    {pkg.avatarCount === 1 ? '1 Avatar' : `${pkg.avatarCount} Avatare`} + {pkg.imageCount} Bilder
                  </span>
                </div>
              )}
              {pkg.avatarCount === 0 && pkg.imageCount > 0 && (
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: accentColor }}></div>
                  <span className="text-white/70">{pkg.imageCount} Bilder</span>
                </div>
              )}
              {pkg.subtext && (
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: accentColor }}></div>
                  <span className="text-white/70">{pkg.subtext}</span>
                </div>
              )}
            </div>

            {/* Buy Button */}
            {!previewMode ? (
              <button
                onClick={() => onBuyClick && onBuyClick(pkg)}
                className="w-full py-2.5 rounded-lg font-medium transition-all hover:opacity-90"
                style={{ backgroundColor: accentColor, color: '#000' }}
              >
                Jetzt kaufen
              </button>
            ) : (
              <div className="w-full py-2.5 rounded-lg font-medium text-center bg-white/10 text-white/50">
                Vorschau
              </div>
            )}
          </div>
        </div>

        {/* Badges */}
        {pkg.isPopular && (
          <div className="absolute top-4 right-4 px-3 py-1 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full">
            <p className="text-black font-bold text-xs">Bestseller</p>
          </div>
        )}
      </div>
    );
  }

  // Template: Minimalist
  if (template === 'minimalist') {
    return (
      <div
        className={`relative p-8 rounded-lg border transition-all duration-300 bg-black ${
          !previewMode ? 'hover:border-white/40' : ''
        }`}
        style={{
          borderColor: pkg.isPopular ? accentColor : 'rgba(255,255,255,0.1)',
          borderWidth: '1px'
        }}
        data-testid={`package-${pkg.id}`}
      >
        {/* Badge minimalistisch */}
        {pkg.isPopular && (
          <div 
            className="absolute top-0 right-0 px-3 py-1 text-xs font-bold rounded-bl-lg"
            style={{ 
              backgroundColor: accentColor,
              color: '#000'
            }}
          >
            BELIEBT
          </div>
        )}

        {/* Name klein */}
        <p className="text-sm uppercase tracking-wider text-white/50 mb-2">{pkg.name}</p>

        {/* Preis sehr groß */}
        <div className="mb-6">
          <p className="text-5xl font-light">€{pkg.price.toFixed(2)}</p>
        </div>

        {/* Details clean */}
        <div className="space-y-3 mb-6 text-sm text-white/70">
          {pkg.avatarCount > 0 && (
            <p>{pkg.avatarCount === 1 ? '1 Avatar' : `${pkg.avatarCount} Avatare`} + {pkg.imageCount} Bilder</p>
          )}
          {pkg.avatarCount === 0 && pkg.imageCount > 0 && (
            <p>{pkg.imageCount} Bilder</p>
          )}
          {pkg.subtext && <p className="text-white/50">{pkg.subtext}</p>}
        </div>

        {/* Description */}
        <p className="text-sm text-white/60 mb-6 leading-relaxed">{pkg.description}</p>

        {/* Buy Button minimalistisch */}
        {!previewMode ? (
          <button
            onClick={() => onBuyClick && onBuyClick(pkg)}
            className="w-full py-3 border transition-all hover:bg-white/5"
            style={{ 
              borderColor: accentColor,
              color: accentColor
            }}
          >
            Auswählen
          </button>
        ) : (
          <div className="w-full py-3 border border-white/10 text-center text-white/50">
            Vorschau
          </div>
        )}
      </div>
    );
  }

  return null;
}
